// ChatHeader.jsx
import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
// Import icons from @tabler/icons-react directly or from your consolidated ChatIcons.js
import {
  IconTrash, IconX, IconArrowLeft, IconSearch, IconChevronLeft,
  IconDotsVertical, IconInfoCircle, IconPhone, IconShare, IconBan,
  IconUserPlus // <--- NEW: For 'Add Participant' icon
} from '@tabler/icons-react'; // Example imports for Tabler Icons
import { Avatar } from "@mantine/core";

// --- Standalone Chat Header Component with Search & Select Mode ---
const ChatHeader = ({
  activeConversation,
  currentUserId,
  typingUsers,
  isSelectModeActive = false,
   onDeleteConversationRequest,
  selectedCount = 0,
  onCancelSelectMode = () => console.log("Cancel Select Mode"),
  onSelectAll = (isChecked) => console.log("Select All:", isChecked),
  onDeleteSelected = () => console.log("Delete Selected"),
  onCloseConversation = () => console.log("Close conversation clicked (Mobile)"),
  onAvatarClick = () => console.log("Avatar clicked"),
  onNameClick = () => console.log("Name clicked"),
  onSearchTermChange = (term) => console.log("Search term:", term),
  onProfileInfoClick = () => console.log("Profile Info clicked"), // Original
  onVoiceCallClick = () => console.log("Voice Call clicked"),
  onSharedMediaClick = () => console.log("Shared Media clicked"),
  onBlockContactClick = () => console.log("Block Contact clicked"),
  // --- NEW PROP for Group Management ---
  onManageGroupMembersClick = () => console.log("Manage Group Members clicked"),
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);
  const searchInputRef = useRef(null);

  // --- Derive display data from activeConversation prop ---
   const { userName, avatarUrl, displayId, lastSeenDisplay, conversationType, participants, isNamelessGroup } = useMemo(() => {
        if (!activeConversation) {
             return { userName: 'Select a conversation', avatarUrl: '', lastSeenDisplay: '', conversationType: null, participants: [], isNamelessGroup: false };
        }

        let displayUserName = activeConversation.title;
        let displayAvatarUrl = activeConversation.avatar;
        let displayLastSeen = '';
        let type = activeConversation.type;
        let displayId;

        if (activeConversation.type === 'direct') {
             const otherParticipant = activeConversation.participants?.find(p => String(p.id) !== String(currentUserId));
             if (otherParticipant) {
                  displayId = otherParticipant.id
                  displayUserName = otherParticipant.name;
                  displayAvatarUrl = otherParticipant.avatar;
                  displayLastSeen = otherParticipant.status === 'online' ? 'Online' : (otherParticipant.lastSeen ? otherParticipant.lastSeen : '');
             } else {
                  displayUserName = 'Direct Chat';
                  displayAvatarUrl = '';
                  displayLastSeen = '';
             }
        } else if (activeConversation.type === 'group') {
             displayLastSeen = activeConversation.participants ? `${activeConversation.participants.length} members` : '';
        }

        return {
            userName: displayUserName,
            displayId: displayId,
            avatarUrl: displayAvatarUrl,
            lastSeenDisplay: displayLastSeen,
            conversationType: type,
            participants: activeConversation.participants,
            isNamelessGroup: activeConversation.type === 'group' && (!displayUserName || displayUserName.trim() === '')
        };

   }, [activeConversation, currentUserId]);

    // --- Calculate Typing Status Display Text ---
    const typingStatusDisplay = useMemo(() => {
        if (!activeConversation?.id || !typingUsers?.[activeConversation.id]) {
            return null;
        }

        const usersTypingInConvoIds = Object.keys(typingUsers[activeConversation.id])
            .filter(userId => String(userId) !== String(currentUserId));

        const isAnyoneTypingHere = usersTypingInConvoIds.length > 0;

        if (!isAnyoneTypingHere) {
            return null;
        }

        if (activeConversation.type === 'direct') {
            const otherParticipant = activeConversation.participants?.find(p =>
                String(p.id) !== String(currentUserId) &&
                usersTypingInConvoIds.some(typingId => String(typingId) === String(p.id))
            );
            if (otherParticipant) {
                return `${otherParticipant.name} is typing...`;
            } else {
                return 'Someone is typing...';
            }
        } else if (activeConversation.type === 'group') {
            const typingCount = usersTypingInConvoIds.length;
            return `${typingCount} typing...`;
        }

        return null;
    }, [activeConversation, typingUsers, currentUserId]);


  // --- Handlers ---
  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
    if (!isMenuOpen) setIsSearchActive(false);
  };

  const handleMenuItemClick = (callback) => {
    callback();
    setIsMenuOpen(false);
  };

  const handleSearchIconClick = () => {
    if (isSelectModeActive) return;
    setIsSearchActive(true);
    setIsMenuOpen(false);
  };

  const handleCloseSearch = () => {
    setIsSearchActive(false);
    setSearchTerm("");
    onSearchTermChange(""); // Ensure search term is cleared in parent
  };

  const handleSearchChange = (event) => {
    const newTerm = event.target.value;
    setSearchTerm(newTerm);
    onSearchTermChange(newTerm);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    onSearchTermChange("");
    searchInputRef.current?.focus();
  };

  const handleSelectAllChange = (event) => {
    onSelectAll(event.target.checked);
  };

  // Effect for closing menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };
    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  // Effect to focus search input
  useEffect(() => {
    if (isSearchActive && !isSelectModeActive) {
      searchInputRef.current?.focus();
    }
  }, [isSearchActive, isSelectModeActive]);

  // Close search/menu when entering select mode
  useEffect(() => {
    if (isSelectModeActive) {
      setIsSearchActive(false);
      setIsMenuOpen(false);
    }
  }, [isSelectModeActive]);

  const handleDeleteConversationClick = useCallback(
    (id) => {
      if (
        window.confirm(`Are you sure you want to delete this conversation?`)
      ) {
        if (onDeleteConversationRequest) onDeleteConversationRequest(activeConversation.id);
        else
          console.warn(
            "onDeleteConversationRequest prop not provided to ChatSidebar"
          );
      }
      setIsMenuOpen(false);
    },
    [onDeleteConversationRequest]
  );

  // --- Render JSX ---
  return (
    <div className="w-full bg-white border-b border-gray-200 transition-all duration-200">
      {isSelectModeActive ? (
        // --- SELECT MODE HEADER VIEW ---
        <div className="w-full min-h-[4rem] md:min-h-[5.25rem] px-3 md:px-5 py-3 md:py-4">
          <div className="w-full flex justify-between items-center">
            {/* Left Side: Select All */}
            <div className="flex items-center">
              <div className="relative flex justify-center items-center mr-2 md:mr-3">
                <input
                  id="select-all"
                  className="h-5 w-5 cursor-pointer outline-none rounded-[.3125rem] border border-indigo-400 accent-indigo-500 bg-gray-100 focus:ring-2 focus:ring-indigo-300"
                  type="checkbox"
                  checked={selectedCount > 0}
                  onChange={handleSelectAllChange}
                  aria-label="Select all messages"
                />
              </div>
              <label htmlFor="select-all" className="cursor-pointer">
                <p className="outline-none text-sm text-black font-medium">
                  {selectedCount > 0 ? `${selectedCount} Selected` : "Select Messages"}
                </p>
              </label>
            </div>
            {/* Right Side: Actions */}
            <div className="flex items-center gap-2 md:gap-3">
              <button
                onClick={onDeleteSelected}
                disabled={selectedCount === 0}
                className="group p-2 px-3 flex justify-center items-center rounded-md transition-colors duration-150 ease-out outline-none text-sm focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed text-red-600 hover:bg-red-50 disabled:hover:bg-transparent"
                aria-label={`Delete ${selectedCount} selected messages`}
                title="Delete selected messages"
              >
                <IconTrash className="h-4 w-4 mr-1 md:mr-1.5" />
                Delete
              </button>
              <button
                onClick={onCancelSelectMode}
                className="group p-2 px-3 flex justify-center items-center rounded-md transition-colors duration-150 ease-out outline-none text-sm focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1 text-indigo-600 hover:bg-indigo-50"
                aria-label="Cancel selection mode"
                title="Cancel selection mode"
              >
                <IconX className="w-4 h-4 mr-1 md:mr-1.5" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : (
        // --- DEFAULT HEADER / SEARCH VIEW ---
        <div
          className={`w-full min-h-[4rem] md:min-h-[5.25rem] px-3 md:px-5 py-3 md:py-4 flex items-center ${
            isSearchActive ? "gap-2" : ""
          }`}
        >
          {isSearchActive ? (
            // --- SEARCH VIEW --- (Only shown if not in Select Mode)
            <>
              {/* Back/Close Search Button */}
              <button
                onClick={handleCloseSearch}
                className="group flex-shrink-0 flex justify-center items-center rounded-full outline-none focus:outline-none hover:bg-gray-100 focus:bg-gray-100 transition-all duration-200 w-8 h-8"
                title="Close search"
                aria-label="Close search"
              >
                <IconArrowLeft className="w-5 h-5 text-gray-500 group-hover:text-indigo-500" />
              </button>
              {/* Search Input Container */}
              <div className="relative flex-grow">
                {/* Search Icon */}
                <i className="absolute left-0 top-1/2 transform -translate-y-1/2 ml-3 text-center pointer-events-none">
                  <IconSearch className="w-5 h-5 stroke-2 text-black opacity-40" />
                </i>
                {/* Input */}
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search messages..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-full h-9 py-2 pl-10 pr-8 border outline-none rounded-md text-sm text-black placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-indigo-400 duration-150 transition ease-out border-gray-300 bg-gray-100 focus:bg-white"
                  aria-label="Search messages in this conversation"
                />
                {/* Clear Button */}
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-0 top-1/2 transform -translate-y-1/2 mr-2 p-1 rounded-full hover:bg-gray-200 text-gray-500 hover:text-gray-700"
                    title="Clear search"
                    aria-label="Clear search"
                  >
                    <IconX className="w-4 h-4" />
                  </button>
                )}
              </div>
            </>
          ) : (
            // --- DEFAULT HEADER VIEW --- (Only shown if not in Select Mode)
            <>
              {/* Back Button (Mobile Only) */}
              <div className="group mr-3 md:hidden flex-shrink-0">
                <button
                  onClick={onCloseConversation}
                  className="group flex justify-center items-center rounded-full outline-none focus:outline-none hover:bg-gray-100 focus:bg-gray-100 transition-all duration-200 w-8 h-8"
                  title="Close conversation"
                  aria-label="Close conversation"
                >
                  <IconChevronLeft className="w-5 h-5 text-gray-500 group-hover:text-indigo-500" />
                </button>
              </div>
              {/* User Info */}
              <div className="flex grow items-center min-w-0 mr-3">
                <button
                   onClick={() =>
                    displayId &&
                    (window.location.href = `/profile/user/${displayId}`)
                  }
                  className="mr-3 md:mr-4 flex-shrink-0 outline-none rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-400"
                  aria-label="View profile"
                  disabled={!displayId}
                >
                  {/* Avatar */}
                    {!isNamelessGroup ? (
                         avatarUrl ? (
                             <img
                               className="w-10 h-10 object-cover rounded-full"
                               src={avatarUrl}
                               alt={`${userName || 'Conversation'} avatar`}
                             />
                         ) : (
                              <Avatar
                                  radius="xl"
                                  size="lg"
                                  alt={userName}
                              >
                                  {userName ?
                                      userName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() :
                                      '?'
                                  }
                              </Avatar>
                         )
                    ) : (
                       <></>
                    )}
                </button>
                {/* Conversation Name or Participant Avatars in Name Spot */}
                <div className="flex flex-col min-w-0">
                  {isNamelessGroup ? (
                       <div onClick={onNameClick} className="cursor-pointer truncate flex items-center" title="Group Participants" tabIndex={0}>
                            <Avatar.Group spacing="xs">
                                {participants.slice(0, 5).map(participant => {
                                    const initials = participant.name ?
                                        participant.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() :
                                        '?';

                                    return (
                                        <Avatar
                                            key={participant.id}
                                            src={participant.avatar || undefined}
                                            alt={participant.name}
                                            radius="xl"
                                            size="md"
                                        >
                                            {!participant.avatar && initials}
                                        </Avatar>
                                    );
                                })}
                                {participants.length > 5 && (
                                    <Avatar radius="sm" size="sm">{`+${participants.length - 5}`}</Avatar>
                                )}
                            </Avatar.Group>
                       </div>
                   ) : (
                        <p
                          onClick={onNameClick}
                          className="outline-none text-sm md:text-base text-black font-semibold leading-tight tracking-tight mb-1 cursor-pointer truncate"
                          title={userName}
                          tabIndex={0}
                        >
                          {userName}
                        </p>
                   )}

                  {/* Status or Participant Avatars Below Name */}
                  {typingStatusDisplay ? (
                      <p className="outline-none text-xs md:text-sm text-indigo-600 leading-tight tracking-tight font-light truncate italic">
                          {typingStatusDisplay}
                      </p>
                  ) : (
                      !isNamelessGroup && conversationType === "group" && participants && participants.length > 0 ? (
                          <div className="flex -space-x-2 overflow-hidden">
                            {participants.slice(0, 3).map(
                              (participant, index) => {
                                  const initials = participant.name ?
                                      participant.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() :
                                      '?';
                                return (
                                  <Avatar
                                    key={participant.id}
                                    className="inline-block ring-2 ring-white"
                                    size="md"
                                    radius="xl"
                                    src={participant.avatar || undefined}
                                    alt={participant.name}
                                  >
                                     {!participant.avatar && initials}
                                  </Avatar>
                                );
                              }
                            )}
                            {participants.length > 3 && (
                              <Avatar
                                className="inline-block ring-2 ring-white"
                                size="md"
                                radius="xl"
                              >
                                +{participants.length - 3}
                              </Avatar>
                            )}
                        </div>
                      ) : (
                          <p
                            className="mt-1 outline-none text-xs md:text-sm text-gray-500 leading-tight tracking-tight font-light truncate"
                            tabIndex={0}
                            aria-label={`Status: ${lastSeenDisplay}`}
                            title={lastSeenDisplay}
                          >
                            {lastSeenDisplay}
                          </p>
                      )
                  )}
                </div>
              </div>
              {/* Action Buttons */}
              <div className="flex flex-shrink-0 items-center">
                {/* Search Icon Button */}
                <button
                  onClick={handleSearchIconClick}
                  className="group flex justify-center items-center rounded-full outline-none focus:outline-none hover:bg-gray-100 focus:bg-gray-100 transition-all duration-200 w-8 h-8 mr-2"
                  title="Search messages"
                  aria-label="Search messages"
                >
                  <IconSearch className="w-5 h-5 text-gray-500 group-hover:text-indigo-500" />
                </button>
                {/* Menu Button and Dropdown */}
                <div className="relative">
                  <button
                    ref={menuButtonRef}
                    onClick={toggleMenu}
                    className="group flex justify-center items-center rounded-full outline-none focus:outline-none hover:bg-gray-100 focus:bg-gray-100 transition-all duration-200 w-8 h-8"
                    id="open-conversation-menu"
                    tabIndex={0}
                    aria-expanded={isMenuOpen}
                    aria-controls="conversation-menu"
                    title="Conversation menu"
                    aria-label="Conversation menu"
                  >
                    <IconDotsVertical className="w-5 h-5 text-gray-500 group-hover:text-indigo-500" />
                  </button>
                  {/* Dropdown Menu */}
                  {isMenuOpen && (
                    <div
                      ref={menuRef}
                      id="conversation-menu"
                      className="absolute right-0 z-50 w-56 mt-2 rounded-md bg-white shadow-lg border border-gray-200 focus:outline-none origin-top-right"
                      role="menu"
                      aria-orientation="vertical"
                      aria-labelledby="open-conversation-menu"
                      tabIndex="-1"
                    >
                      <div className="py-1" role="none">
                        {/* Conditional: Group Info / Profile Information */}
                        {activeConversation?.type === 'group' && (
                          <button
                            onClick={() => handleMenuItemClick(onManageGroupMembersClick)} // <--- NEW: Call new handler here
                            className="w-full px-4 py-2 flex items-center text-left outline-none text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 focus:bg-gray-100"
                            role="menuitem"
                          >
                            <IconInfoCircle className="h-5 w-5 mr-3 text-gray-500" />
                            Manage Group
                          </button>
                        ) }

                        {/* Optional: Direct "Add Participant" button (if not going through Group Info) */}
                        {activeConversation?.type === 'group' && activeConversation.is_admin && ( // Add `isCurrentUserAdminOrCreator` if this button shouldn't always show
                            <button
                                onClick={() => handleMenuItemClick(onManageGroupMembersClick)} // Still opens the same modal
                                className="w-full px-4 py-2 flex items-center text-left outline-none text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 focus:bg-gray-100"
                                role="menuitem"
                            >
                                <IconUserPlus size="1rem" className="h-5 w-5 mr-3 text-gray-500" />
                                Add Participant
                            </button>
                        )}


                        {/* <button
                          onClick={() => handleMenuItemClick(onVoiceCallClick)}
                          className="w-full px-4 py-2 flex items-center text-left outline-none text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 focus:bg-gray-100"
                          role="menuitem"
                        >
                          <IconPhone className="h-5 w-5 mr-3 text-gray-500" />
                          Voice call
                        </button>
                        <button
                          onClick={() => handleMenuItemClick(onSharedMediaClick)}
                          className="w-full px-4 py-2 flex items-center text-left outline-none text-sm text-gray-700 transition-colors duration-150 hover:bg-gray-100 focus:bg-gray-100"
                          role="menuitem"
                        >
                          <IconShare className="h-5 w-5 mr-3 text-gray-500" />
                          Shared media
                        </button> */}
                        <div className="my-1 border-t border-gray-100"></div>
                        <button
                          onClick={ handleDeleteConversationClick}
                          className="w-full px-4 py-2 flex items-center text-left outline-none text-sm text-red-600 transition-colors duration-150 hover:bg-red-50 focus:bg-red-50"
                          role="menuitem"
                        >
                          <IconBan className="h-5 w-5 mr-3" />
                          Delete Conversation
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatHeader;